import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import useAtsApplicationsApi, { APPLICATION_STATUSES } from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import A11yDialog from '../../components/ui/A11yDialog';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import OfferCreateDialog from './OfferCreateDialog';
import ScopeBanner from './ScopeBanner';
import {
  resolveCanManage,
  canMoveStage,
  visibleTransitions,
  canOffer,
  canDelete,
} from './applicationActions';
import { useAuth } from '../../context/AuthContext';
import PoolSelectField, { POOL_ELIGIBLE_ACTIONS } from '../ats-candidate-pools/components/PoolSelectField';
import { toast } from 'sonner';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DialogMessage = styled.p`
  margin: 0 0 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 14px;
  line-height: 1.5;
`;

// Toplu seçim çubuğu — MyGoalsPage'in #71 desenidir (sayfa-yerel checkbox kolonu +
// listenin üstünde yalnız ≥1 satır seçiliyken çizilen şerit). DataTable.js'in kendi
// seçim mekanizması KULLANILMADI: o mekanizma yalnız `onRowsDelete`'e kablolanmış
// (bkz. DataTable.js `selectableRows`) ve tek eylemi silmek.
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid rgba(2, 145, 239, 0.18);
  border-radius: 6px;
`;

const BulkCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Seçimin KAPSAMI arayüzde yazılı durur: "tümünü seç" tüm sonuç kümesini değil
// GÖRÜNEN SAYFAYI kapsar. Yazılmazsa kullanıcı 900 satırın hepsini seçtiğini sanır.
const BulkScopeNote = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const BulkSpacer = styled.span`
  flex: 1;
`;

const BulkSelectCell = styled.div``;

const ResultBox = styled.div`
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 6px;
  background: #fff;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ResultSummary = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DismissBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const FailedTitle = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #B02A37;
`;

const FailedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #B02A37;
  padding: 6px 0;
  border-bottom: 1px solid #F1F5F9;
  &:last-child { border-bottom: none; }
`;

// Durum geçişleri, silinebilir durumlar ve aksiyon görünürlüğü artık
// `applicationActions.js`te — liste satır menüsü ile başvuru detayının Genel Bakış
// şeridi AYNI kuralları çizer (iki kopya kaçınılmaz olarak ayrışırdı).

// Toplu seçime AÇIK durumlar: iki toplu eylemin (aşama taşıma = yalnız `active`,
// diskalifiye = `active`|`on_hold`) BİRLEŞİMİ. Kapanmış satır hiçbirine uygun
// olmadığından checkbox HİÇ çizilmez. `on_hold` satır seçilip "Aşamaya Taşı"
// denirse sunucu onu `cannotMoveOnHold` ile düşürür ve sonuç panelinde
// satır-bazlı neden GÖSTERİLİR — kısmi başarı tasarımın kendisidir.
const BULK_SELECTABLE_STATUSES = ['active', 'on_hold'];

// Derin bağlantı kapsamı: İlanlar sayfası "Başvuruları Gör" (?jobPostingId=…) ve Adaylar
// sayfası "Başvuruları" (?candidateId=…) listeyi tek ilana/adaya daraltır. İlan seçici
// (FE-15) de AYNI kapsamı yazar — tek kavram, tek kontrol.
// `?status=` CSV kapsamı: İşe Alım Panosu'nun KPI şeridi buraya derin bağlanır
// ("Aktif Başvurular" → active,on_hold,offered; "Dönemde İşe Alınan" → accepted).
// Sunucu anahtarı `status[in]` CSV'dir (AtsApplicationFilterRequest.StatusIn) —
// tablo süzgecinin ÜRETTİĞİ anahtarın AYNISI, ayrı bir yol açılmaz. Tanınmayan
// değerler ATILIR: URL'den gelen serbest metin sunucuya sızmaz.
const readStatusScope = (raw) => {
  const list = (raw || '').split(',').map((x) => x.trim())
    .filter((x) => APPLICATION_STATUSES.includes(x));
  return list.length ? list.join(',') : undefined;
};

const readScope = (search) => {
  const q = new URLSearchParams(search || '');
  const scope = {};
  const jobPostingId = q.get('jobPostingId');
  const candidateId = q.get('candidateId');
  const status = readStatusScope(q.get('status'));
  if (jobPostingId) scope.jobPostingId = jobPostingId;
  if (candidateId) scope.candidateId = candidateId;
  if (status) scope.status = status;
  return scope;
};
const SCOPE_PARAM_KEYS = ['jobPostingId[eq]', 'candidateId[eq]', 'status[in]'];
const scopeParams = (scope) => ({
  ...(scope.jobPostingId ? { 'jobPostingId[eq]': scope.jobPostingId } : {}),
  ...(scope.candidateId ? { 'candidateId[eq]': scope.candidateId } : {}),
  ...(scope.status ? { 'status[in]': scope.status } : {}),
});
const withoutScope = (params) => {
  const next = { ...params };
  SCOPE_PARAM_KEYS.forEach((k) => delete next[k]);
  return next;
};
const sameScope = (a, b) => a.jobPostingId === b.jobPostingId
  && a.candidateId === b.candidateId
  && a.status === b.status;
const scopeSearch = (scope) => {
  const q = new URLSearchParams();
  if (scope.candidateId) q.set('candidateId', scope.candidateId);
  if (scope.jobPostingId) q.set('jobPostingId', scope.jobPostingId);
  if (scope.status) q.set('status', scope.status);
  const s = q.toString();
  return s ? `?${s}` : '';
};

export default function AtsApplicationsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;
  const history = useHistory();
  const location = useLocation();

  // Kapsam ref'te yaşar ve handleDataStateChange her sorguya karıştırır — sayfa/sıralama/
  // filtre değişimi kapsamı DÜŞÜRMEZ (eskiden 2. sayfa kiracının tüm başvurularını
  // gösteriyordu). İlk sorgu da kapsamlı kurulur: kapsamsız bir ilk istek hiç atılmaz.
  const scopeRef = useRef(readScope(location.search));
  const [queryParams, setQueryParams] = useState(() => ({
    pageSize: 10, pageNumber: 1, ...scopeParams(scopeRef.current),
  }));
  const [filterList, setFilterList] = useState([]);
  // Afiş etiketleri (ilan başlığı / aday adı) — router state'inden ya da GET yedeğinden.
  const [postingTitle, setPostingTitle] = useState('');
  const [candidateName, setCandidateName] = useState('');

  // rows: tekil satır akışında tek elemanlı, toplu akışta seçilenlerin tamamı.
  // Tek diyalog iki akışa da hizmet eder — üçüncü bir modal deseni EKLENMEZ.
  const [moveDialog, setMoveDialog] = useState({ isOpen: false, rows: [] });
  const [moveStages, setMoveStages] = useState([]);
  // Snapshot of current stages' OrderIndex captured at dialog open — used to
  // detect backwards moves (target OrderIndex < current) so we can warn the
  // user before regressing a candidate to an earlier stage.
  const [currentStageOrderIndex, setCurrentStageOrderIndex] = useState(null);
  const [moveStageId, setMoveStageId] = useState('');
  const [moveNote, setMoveNote] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);
  // Confirm dialog for backwards stage moves (regression).
  const [backwardConfirm, setBackwardConfirm] = useState({ isOpen: false });

  const [statusDialog, setStatusDialog] = useState({ isOpen: false, rows: [], action: '', actionLabel: '', requiresReason: false });
  const [statusReason, setStatusReason] = useState('');
  // SPEC F2 — tekil reddet/diskalifiyede opsiyonel havuz. Toplu uç (`bulk-status`)
  // `addToPoolId` taşımaz; seçici yalnız TEK satırlık akışta çizilir.
  const [statusPoolId, setStatusPoolId] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, candidate: '' });
  const [deleting, setDeleting] = useState(false);

  const [offerDialog, setOfferDialog] = useState({ isOpen: false, application: null });

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);

  // Afiş UYGULANAN parametrelerden türer (DataTable filterList'i yutar; çip yolu güvenilmez).
  const appliedPostingId = queryParams['jobPostingId[eq]'] || null;
  const appliedCandidateId = queryParams['candidateId[eq]'] || null;
  const appliedStatuses = queryParams['status[in]'] || null;

  const {
    fetching, loadFailed, data, pagination, columns: baseColumns,
    moveToStage, changeStatus, remove, fetchStagesForPosting,
    fetchPostingTitle, fetchCandidateName,
    bulkMoveToStage, bulkChangeStatus, searchJobPostings,
  } = useAtsApplicationsApi(queryParams, appliedPostingId);

  // URL kapsamı sonradan değişirse (aynı rotada geri/ileri) ref + sorgu yeniden eşitlenir;
  // açılışta ve "Kapsamı Kaldır"ın history.replace'inde kapsam zaten eşit → dokunulmaz.
  useEffect(() => {
    const urlScope = readScope(location.search);
    if (sameScope(urlScope, scopeRef.current)) return;
    scopeRef.current = urlScope;
    setQueryParams((prev) => ({ ...withoutScope(prev), pageNumber: 1, ...scopeParams(urlScope) }));
  }, [location.search]);

  // Etiketler: İlanlar/Adaylar sayfası router state'iyle taşır; doğrudan URL'de (yenileme)
  // tek alanlık GET yedeği. Yalnız kapsam kimliği değişince çözülür, tablo değişiminde değil.
  useEffect(() => {
    if (!appliedPostingId) { setPostingTitle(''); return undefined; }
    const filter = location.state?.postingFilter;
    if (filter?.id === appliedPostingId && filter.title) { setPostingTitle(filter.title); return undefined; }
    let alive = true;
    fetchPostingTitle(appliedPostingId).then((title) => { if (alive) setPostingTitle(title || ''); });
    return () => { alive = false; };
  }, [appliedPostingId, location.state, fetchPostingTitle]);

  useEffect(() => {
    if (!appliedCandidateId) { setCandidateName(''); return undefined; }
    const filter = location.state?.candidateFilter;
    if (filter?.id === appliedCandidateId && filter.name) { setCandidateName(filter.name); return undefined; }
    let alive = true;
    fetchCandidateName(appliedCandidateId).then((name) => { if (alive) setCandidateName(name || ''); });
    return () => { alive = false; };
  }, [appliedCandidateId, location.state, fetchCandidateName]);

  // Seçim sayfa/sıralama/filtre/kapsam değişince VE liste yeniden yüklenince DÜŞER.
  // DataTable.js'in kendi "Reset selection when data changes" davranışının aynısı,
  // ama SORGUYA da bağlı: yanıt gövdesi birebir aynı diziyi döndürdüğünde (aynı
  // referans) yalnız `data`'ya bakan bir efekt HİÇ tetiklenmez ve 2. sayfada
  // görünmeyen satırlar seçili kalırdı. Seçim SAYFA kapsamlıdır — bu onun bekçisi.
  useEffect(() => { setSelectedIds([]); }, [data, queryParams]);

  const handleRowClick = useCallback((row) => {
    if (row?.id) history.push(`/ats/applications/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams({ ...parseTableParams(tableState, baseColumns), ...scopeParams(scopeRef.current) });
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // İlan seçici ve "Kapsamı Kaldır" AYNI kapsam yazıcısını kullanır: ref + sorgu +
  // URL (yenilemede kapsam korunsun / geri gelmesin) tek yerden güncellenir.
  const applyScope = useCallback((nextScope) => {
    scopeRef.current = nextScope;
    setQueryParams((prev) => ({ ...withoutScope(prev), pageNumber: 1, ...scopeParams(nextScope) }));
    const search = scopeSearch(nextScope);
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search, state: undefined });
    }
  }, [history, location.pathname, location.search]);

  const clearScope = useCallback(() => applyScope({}), [applyScope]);

  const handlePostingPick = useCallback((id) => {
    applyScope({ ...scopeRef.current, jobPostingId: id || undefined });
  }, [applyScope]);

  // İlan etiketi AĞDAN DEĞİL, elimizdeki iki kaynaktan çözülür: seçicinin kendi
  // arama sonucu ve afişin zaten çözdüğü başlık. Ekstra bir GET atmak, kapsam
  // afişinin router-state yolunu (ilan GET'i atılmaz) bozardı.
  const pickedLabelsRef = useRef({});
  const getPostingById = useCallback(async (id) => {
    const label = pickedLabelsRef.current[id]
      || (id === appliedPostingId ? postingTitle : '');
    return label ? { id, label } : null;
  }, [appliedPostingId, postingTitle]);

  const handlePostingSearch = useCallback(async (term) => {
    const rows = await searchJobPostings(term);
    rows.forEach((r) => { pickedLabelsRef.current[r.id] = r.label; });
    return rows;
  }, [searchJobPostings]);

  // ─── Selection ───────────────────────────────────────────────────────────
  const selectableRows = useMemo(
    () => (data || []).filter((r) => BULK_SELECTABLE_STATUSES.includes(r.status)),
    [data],
  );
  const selectedRows = useMemo(
    () => (data || []).filter((r) => selectedIds.includes(r.id)),
    [data, selectedIds],
  );
  // Toplu aşama taşıma tek bir ilanın efektif aşama listesini gerektirir: iki farklı
  // ilanın aşama kümeleri aynı olmak ZORUNDA değildir (ilana özel liste, PARTİ 3).
  const selectedPostingIds = useMemo(
    () => [...new Set(selectedRows.map((r) => r.jobPostingId))],
    [selectedRows],
  );
  const bulkMoveAllowed = selectedPostingIds.length === 1;

  const toggleSelect = useCallback((id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);

  // "Tümünü seç" = GÖRÜNEN SAYFANIN uygun satırları. Tüm sonuç kümesi DEĞİL:
  // sunucu tek istekte en çok 50 hedef kabul eder ve sayfa boyu seçenekleri
  // (10/20/50) bu sınırın altındadır — yani bu buton hiçbir zaman sınırı aşamaz.
  const selectAllOnPage = useCallback(() => {
    setSelectedIds(selectableRows.map((r) => r.id));
  }, [selectableRows]);

  // ─── Move stage flow ─────────────────────────────────────────────────────
  const openMoveDialog = useCallback(async (rows) => {
    if (!rows || rows.length === 0) return;
    setMoveDialog({ isOpen: true, rows });
    setMoveStageId('');
    setMoveNote('');
    setMoveStages([]);
    setCurrentStageOrderIndex(null);
    const stages = await fetchStagesForPosting(rows[0].jobPostingId);
    // Capture the EARLIEST current-stage OrderIndex among the selected rows BEFORE
    // filtering them out — a target below it means at least one row regresses.
    // If no row sits on a known stage, leave it null ("no backwards possible").
    const currentIds = new Set(rows.map((r) => r.currentStageId).filter(Boolean));
    const orders = stages.filter((s) => currentIds.has(s.id)).map((s) => s.orderIndex);
    setCurrentStageOrderIndex(orders.length > 0 ? Math.max(...orders) : null);
    // Exclude the stage every selected row is already on (single-row flow: its own stage).
    setMoveStages(stages.filter((s) => !(currentIds.size === 1 && currentIds.has(s.id))));
  }, [fetchStagesForPosting]);

  // BP-26 — hedef aşama terminal "Elendi" ise gerekçe ZORUNLU: o geçiş başvuruyu
  // `disqualified` yapar ve backend gerekçesiz isteği 400
  // (ats.application.disqualifyReasonRequired) ile reddeder. Bayrak seçili hedeften
  // TÜRETİLİR — ayrı bir state ikinci bir doğruluk kaynağı olurdu.
  const moveRequiresReason = useMemo(() => {
    const target = moveStages.find((st) => st.id === moveStageId);
    return !!target && target.isTerminal === true && target.type === 'disqualified';
  }, [moveStages, moveStageId]);

  // Performs the actual move-to-stage API call and closes the dialog. Shared
  // by both the direct (forward) path and the post-confirmation (backward)
  // path so the busy/cleanup logic lives in one place.
  const performMove = useCallback(async () => {
    const rows = moveDialog.rows || [];
    if (rows.length === 0 || !moveStageId) return;
    setMoveBusy(true);
    let ok;
    if (rows.length === 1) {
      ok = await moveToStage(rows[0].id, moveStageId, moveNote || undefined);
    } else {
      // Başlıklar YENİLEMEDEN ÖNCEKİ satırlardan çözülür — refresh() sonrası liste
      // değişeceğinden sonuç panelindeki adlar bu anlık görüntüye sabitlenir.
      const snapshot = rows.reduce((acc, r) => ({ ...acc, [r.id]: r.candidateFullName }), {});
      const res = await bulkMoveToStage(rows.map((r) => r.id), moveStageId, moveNote || undefined);
      if (res) {
        setBulkResult({
          succeededCount: (res.succeeded || []).length,
          failed: (res.failed || []).map((f) => ({ ...f, name: snapshot[f.applicationId] || f.applicationId })),
        });
        setSelectedIds([]);
      }
      ok = !!res;
    }
    // FE-31 — diyalog ESKİDEN KOŞULSUZ kapanıyordu: sunucu isteği reddettiğinde
    // (403, geçersiz aşama, iş kuralı) yazılan gerekçe notu da beraberinde
    // siliniyordu. Kullanıcı interceptor'ın toast'ını okuyor, diyaloğu yeniden
    // açıyor ve her şeyi baştan yazıyordu. Artık yalnız BAŞARIDA kapanır.
    if (!ok) { setMoveBusy(false); return; }
    setMoveDialog({ isOpen: false, rows: [] });
    setCurrentStageOrderIndex(null);
    setMoveBusy(false);
  }, [moveDialog, moveStageId, moveNote, moveToStage, bulkMoveToStage]);

  const handleMoveConfirm = useCallback(() => {
    if (!moveDialog.rows || moveDialog.rows.length === 0 || !moveStageId) return;
    if (moveRequiresReason && !moveNote.trim()) return;
    // Backwards-move detection: target stage's OrderIndex strictly less than
    // the deepest selected row's OrderIndex means at least one regression.
    // Equal/forward proceed silently; backend already rejects same-stage moves.
    const target = moveStages.find((s) => s.id === moveStageId);
    const targetOrder = target ? target.orderIndex : null;
    const isBackward = currentStageOrderIndex != null
      && targetOrder != null
      && targetOrder < currentStageOrderIndex;
    if (isBackward) {
      // Hide the move-stage modal while the confirm dialog is up — they live
      // on different z-index layers (move modal sits ABOVE ConfirmDialog's
      // overlay), so leaving both open would clip the warning. State is
      // preserved on cancel so the user can re-open and pick a different stage.
      setMoveDialog((prev) => ({ ...prev, isOpen: false }));
      setBackwardConfirm({ isOpen: true });
      return;
    }
    performMove();
  }, [moveDialog, moveStageId, moveStages, currentStageOrderIndex, performMove, moveRequiresReason, moveNote]);

  const handleBackwardConfirm = useCallback(() => {
    setBackwardConfirm({ isOpen: false });
    performMove();
  }, [performMove]);

  // ESC, İptal düğmesi ve atma onayı AYNI kapatma yolunu kullanır.
  const closeMoveDialog = useCallback(() => {
    setMoveDialog({ isOpen: false, rows: [] });
    setCurrentStageOrderIndex(null);
  }, []);
  const closeStatusDialog = useCallback(() => {
    setStatusDialog({ isOpen: false, rows: [], action: '', actionLabel: '', requiresReason: false });
  }, []);

  const handleBackwardCancel = useCallback(() => {
    setBackwardConfirm({ isOpen: false });
    // Bring back the move-stage modal with prior selection intact so the user
    // can pick a different stage without re-fetching or re-typing the note.
    setMoveDialog((prev) => ({ ...prev, isOpen: true }));
  }, []);

  // ─── Status change flow ──────────────────────────────────────────────────
  const openStatusDialog = useCallback((rows, transition) => {
    setStatusReason('');
    setStatusDialog({
      isOpen: true,
      rows,
      action: transition.action,
      actionLabel: t(transition.labelKey),
      requiresReason: !!transition.requiresReason,
    });
  }, [t]);

  const handleStatusConfirm = useCallback(async () => {
    const rows = statusDialog.rows || [];
    if (rows.length === 0) return;
    setStatusBusy(true);
    let ok;
    if (rows.length === 1) {
      const poolForBody = writable && POOL_ELIGIBLE_ACTIONS.includes(statusDialog.action)
        ? (statusPoolId || undefined) : undefined;
      ok = await changeStatus(rows[0].id, statusDialog.action, statusReason || undefined, poolForBody);
    } else {
      const snapshot = rows.reduce((acc, r) => ({ ...acc, [r.id]: r.candidateFullName }), {});
      const res = await bulkChangeStatus(rows.map((r) => r.id), statusDialog.action, statusReason || undefined);
      if (res) {
        setBulkResult({
          succeededCount: (res.succeeded || []).length,
          failed: (res.failed || []).map((f) => ({ ...f, name: snapshot[f.applicationId] || f.applicationId })),
        });
        setSelectedIds([]);
      }
      ok = !!res;
    }
    // FE-31 — bkz. performMove: başarısızlıkta gerekçe metni KORUNUR.
    if (!ok) { setStatusBusy(false); return; }
    setStatusDialog({ isOpen: false, rows: [], action: '', actionLabel: '', requiresReason: false });
    setStatusBusy(false);
  }, [statusDialog, statusReason, statusPoolId, writable, changeStatus, bulkChangeStatus]);

  // ─── Delete flow ─────────────────────────────────────────────────────────
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    await remove(deleteDialog.id);
    setDeleteDialog({ isOpen: false, id: null, candidate: '' });
    setDeleting(false);
  }, [deleteDialog, remove]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [{
      type: 'details',
      label: t('ats.application.action.details'),
      onClick: () => history.push(`/ats/applications/${row.id}`),
    }];
    // Süreç aksiyonları (aşama taşıma + durum değiştirme) bugün SALT AT.Write'tır:
    // AtsApplicationDataAccess.MoveToStage ve ChangeStatus `if (!isPrivileged) 403`
    // der; sunucu aynı kapıyı satır başına `canManage` ile bildirir.
    const canManage = resolveCanManage(row, { writable });
    // Yalnız `active` başvuru aşama değiştirebilir: BE `on_hold` için 409
    // `ats.application.cannotMoveOnHold`, diğer (terminal) durumlar için 409
    // `cannotMoveTerminal` döner. Askıdaki satıra bu eylemi sunmak, tıklayanın
    // garanti hata alması demekti — önce askıdan çıkarılır, sonra taşınır.
    if (canMoveStage(row, { canManage })) {
      actions.push({
        type: 'move_stage',
        label: t('ats.application.action.moveStage'),
        onClick: () => openMoveDialog([row]),
      });
    }
    // "Teklif Yap" is also surfaced for `offered` rows whose live offer was
    // withdrawn/rejected/expired — BE re-allows Create and the BE will reject
    // with `ats.offer.liveAlreadyExists` if a live offer still exists.
    if (canOffer(row, { writable })) {
      actions.push({
        type: 'offer',
        label: t('ats.application.action.offer'),
        onClick: () => setOfferDialog({ isOpen: true, application: row }),
      });
    }
    visibleTransitions(row, { canManage }).forEach((tr) => {
      actions.push({
        type: tr.action,
        label: t(tr.labelKey),
        onClick: () => openStatusDialog([row], tr),
      });
    });
    // Silme yalnız KAPANMIŞ başvuruda (BP-15): BE canlı süreçteki satır için 409
    // `ats.application.cannotDeleteStatus` döner. (Kabul edilmiş teklifi olan kapanmış
    // satırı BE ayrıca engeller — listede o bilgi yok, o durumda hata mesajı doğru cevaptır.)
    if (canDelete(row, { writable })) {
      if (actions.length > 1) actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id, candidate: row.candidateFullName }),
      });
    }
    return actions;
  }, [t, writable, openMoveDialog, openStatusDialog, history]);

  const columns = useMemo(() => [
    // Toplu seçim kolonu — yalnız süreç yetkisi olan kullanıcıya ve yalnız toplu
    // eyleme UYGUN satırlarda çizilir (kapanmış satırda checkbox yok).
    ...(writable ? [{
      name: 'bulkSelect',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || !BULK_SELECTABLE_STATUSES.includes(row.status)) return null;
          return (
            // stopPropagation: hücre tıklaması satırın onRowClick'ini (detay sayfası)
            // TETİKLEMEMELİ — tekil akış buradan hiç etkilenmez.
            <BulkSelectCell data-role={`bulk-select-${row.id}`} onClick={(e) => e.stopPropagation()}>
              <CheckboxInput checked={selectedIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
            </BulkSelectCell>
          );
        },
      },
    }] : []),
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildActions, writable, selectedIds, toggleSelect]);

  // Reset reason when dialog closes so the stale value doesn't leak across actions.
  useEffect(() => {
    if (!statusDialog.isOpen) { setStatusReason(''); setStatusPoolId(''); }
  }, [statusDialog.isOpen]);

  const moveDialogCount = (moveDialog.rows || []).length;
  const statusDialogCount = (statusDialog.rows || []).length;

  // "Pano" yalnız TEK bir ilan kapsamındayken anlamlıdır (kanban kolonları o ilanın
  // efektif aşamalarından türer). useMemo ŞART: her render'da yeni bir eleman üretmek
  // aşağıdaki DataTable useMemo'sunu tamamen etkisiz kılardı.
  const toolbarActions = useMemo(() => (appliedPostingId ? (
    <Button
      variant="outline"
      data-role="ats-open-board"
      onClick={() => history.push({
        pathname: `/ats/job-postings/${appliedPostingId}/pipeline`,
        state: { postingTitle },
      })}
    >
      {t('ats.application.board.open')}
    </Button>
  ) : undefined), [appliedPostingId, postingTitle, history, t]);

  return (
    <Container>
      {/* Move-stage dialog — tekil satır ve toplu seçim AYNI diyaloğu kullanır. */}
      <A11yDialog
        open={moveDialog.isOpen && moveDialogCount > 0}
        onClose={closeMoveDialog}
        dirty={!!moveStageId || !!moveNote.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <DialogTitle id={titleId}>
              {moveDialogCount === 1
                ? t('ats.application.moveStageTitle', { candidate: moveDialog.rows[0].candidateFullName })
                : t('ats.application.bulk.moveTitle', { count: moveDialogCount })}
            </DialogTitle>
            <FieldGroup>
              <GenericSelect
                label={t('ats.application.targetStage')}
                options={moveStages}
                value={moveStageId}
                onChange={(id) => setMoveStageId(id || '')}
                placeholder={t('ats.application.selectStage')}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={moveRequiresReason ? t('ats.application.reason') : t('ats.application.reasonNote')}
                type="textarea"
                rows={3}
                value={moveNote}
                onChange={(e) => setMoveNote(e.target.value)}
                required={moveRequiresReason}
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={closeMoveDialog}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleMoveConfirm}
                disabled={!moveStageId || moveBusy || (moveRequiresReason && !moveNote.trim())}
                loading={moveBusy}
              >
                {t('common.confirm')}
              </Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      {/* Status-change dialog */}
      <A11yDialog
        open={statusDialog.isOpen}
        onClose={closeStatusDialog}
        dirty={statusDialog.requiresReason && !!statusReason.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <DialogTitle id={titleId}>
              {statusDialogCount === 1
                ? `${statusDialog.actionLabel} — ${statusDialog.rows[0].candidateFullName}`
                : t('ats.application.bulk.statusTitle', { action: statusDialog.actionLabel, count: statusDialogCount })}
            </DialogTitle>
            {statusDialog.requiresReason && (
              <FieldGroup>
                <TextInput
                  label={t('ats.application.reason')}
                  type="textarea"
                  rows={3}
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  required
                />
              </FieldGroup>
            )}
            {!statusDialog.requiresReason && (
              <DialogMessage>
                {t('ats.application.statusChangeConfirm', { action: statusDialog.actionLabel })}
              </DialogMessage>
            )}
            {writable && statusDialogCount === 1 && POOL_ELIGIBLE_ACTIONS.includes(statusDialog.action) && (
              <FieldGroup>
                <PoolSelectField value={statusPoolId} onChange={setStatusPoolId} />
              </FieldGroup>
            )}
            <Actions>
              <Button variant="outline" onClick={closeStatusDialog}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleStatusConfirm}
                disabled={statusBusy || (statusDialog.requiresReason && !statusReason.trim())}
                loading={statusBusy}
              >
                {t('common.confirm')}
              </Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.application.deleteConfirm', { candidate: deleteDialog.candidate })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, candidate: '' })}
        loading={deleting}
      />

      {/* Backwards-move warning: shown when target stage has lower OrderIndex
          than the current stage. We don't block — recruiters legitimately move
          candidates back when more info arrives — but we make the regression
          deliberate by requiring explicit confirmation. */}
      <ConfirmDialog
        isOpen={backwardConfirm.isOpen}
        title={t('ats.application.backwardStageTitle')}
        message={t('ats.application.backwardStageConfirm')}
        onConfirm={handleBackwardConfirm}
        onCancel={handleBackwardCancel}
        loading={moveBusy}
        type="danger"
      />

      <OfferCreateDialog
        open={offerDialog.isOpen}
        application={offerDialog.application}
        onClose={() => setOfferDialog({ isOpen: false, application: null })}
        onCreated={() => toast.success(t('offer.create.success'))}
      />

      {/* Etiket çözülemezse (silinmiş ilan/aday) kimlik gösterilir — kapsamın açık olduğu
          yine de görünür kalır. */}
      <ScopeBanner
        postingTitle={appliedPostingId ? (postingTitle || appliedPostingId) : ''}
        candidateName={appliedCandidateId ? (candidateName || appliedCandidateId) : ''}
        statusLabels={appliedStatuses
          ? appliedStatuses.split(',')
            .map((sKey) => t(`ats.application.status.${sKey}`, { defaultValue: sKey }))
            .join(', ')
          : ''}
        onClear={clearScope}
        picker={(
          <AsyncGenericSelect
            compact
            value={appliedPostingId}
            onChange={handlePostingPick}
            onSearch={handlePostingSearch}
            onGetById={getPostingById}
            placeholder={t('ats.application.postingFilter')}
            minChars={0}
          />
        )}
      />

      {/* Okuma başarısız olduğunda "kayıt yok" DEĞİL, hata bandı — api.js GET'lerde
          reject etmez, gerçek sinyal res.data.status (utils/apiResult.js). */}
      {loadFailed && <LoadErrorBanner message={t('ats.application.loadFailed')} />}

      {bulkResult && (
        <ResultBox data-role="ats-bulk-result">
          <ResultHeader>
            <ResultSummary data-role="ats-bulk-result-summary">
              {t('ats.application.bulk.resultSummaryLabel')}: {bulkResult.succeededCount}
            </ResultSummary>
            <DismissBtn type="button" aria-label={t('common.close')} onClick={() => setBulkResult(null)}>×</DismissBtn>
          </ResultHeader>
          {bulkResult.failed.length > 0 && (
            <>
              <FailedTitle>{t('ats.application.bulk.resultFailedTitle')}</FailedTitle>
              {bulkResult.failed.map((item) => (
                <FailedRow key={item.applicationId} data-role="ats-bulk-result-failed-row">
                  <span>{item.name}</span>
                  <span>{t(`responseErrors.${item.reason}`, { defaultValue: item.reason })}</span>
                </FailedRow>
              ))}
            </>
          )}
        </ResultBox>
      )}

      {selectedIds.length > 0 && (
        <BulkBar data-role="ats-bulk-bar">
          <BulkCount data-role="ats-bulk-selected-count">
            {t('ats.application.bulk.selectedCountLabel')}: {selectedIds.length}
          </BulkCount>
          {/* Seçimin kapsamı arayüzde AÇIKÇA yazılıdır. */}
          <BulkScopeNote>{t('ats.application.bulk.pageScopeNote')}</BulkScopeNote>
          <BulkSpacer />
          <Button
            type="button"
            variant="outline"
            data-role="ats-bulk-select-all"
            onClick={selectAllOnPage}
          >
            {t('ats.application.bulk.selectAllOnPage', { count: selectableRows.length })}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-role="ats-bulk-clear"
            onClick={() => setSelectedIds([])}
          >
            {t('ats.application.bulk.clearSelection')}
          </Button>
          <Button
            type="button"
            data-role="ats-bulk-move"
            disabled={!bulkMoveAllowed}
            title={bulkMoveAllowed ? undefined : t('ats.application.bulk.singlePostingRequired')}
            onClick={() => openMoveDialog(selectedRows)}
          >
            {t('ats.application.bulk.moveStage')}
          </Button>
          <Button
            type="button"
            data-role="ats-bulk-disqualify"
            onClick={() => openStatusDialog(selectedRows, {
              action: 'disqualify',
              labelKey: 'ats.application.action.disqualify',
              requiresReason: true,
            })}
          >
            {t('ats.application.bulk.disqualify')}
          </Button>
        </BulkBar>
      )}

      {useMemo(() => (
        <DataTable
          title={t('ats.application.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          toolbarActions={toolbarActions}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, toolbarActions,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
